import React, { useEffect, useState } from "react";
import styles from "../EditDiv/EditDiv.module.css";
import buttonStyles from "../YesNoButtonsCSS/YesNoButtons.module.css";
import BlueLogo from "../../assets/BlueLogo.png";
import { Grid, TextField, Alert, Box, Collapse } from "@mui/material";
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import Dropdown from '../DropDownBox/Dropdown';
import { Form, FormikProvider } from "formik";
import { useMsal } from "@azure/msal-react";
import { useFormik } from "formik";
import * as Yup from "yup";
import textfieldStyle from "../../GlobalStyles/styles";
import DatePicker from "../DatePicker/DatePicker";

import { useSelector, useDispatch } from '../../redux/store';
import { getProjectPhases, clearTimesheetResponse } from "../../redux/slices/project";

const TimesheetSubconForm = ({ 
    projectNumber, 
    subcontractorName, 
    projectAddress, 
    onClose, 
    onSubmit,
    editMode = false,
    existingEntry = null 
}) => {
    const { accounts } = useMsal();
    const account = accounts[0];

    const dispatch = useDispatch();

    const { phaseList, timesheetResponse, showSuccess } = useSelector(state => state.project);
    const today = new Date().toISOString().split('T')[0];
    const [showCostFeedback, setShowCostFeedback] = useState(false);
    const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);

    useEffect(() => {
        if (projectNumber) {
            dispatch(getProjectPhases(projectNumber));
        }
    }, [dispatch, projectNumber]);

    // Handle showing cost feedback when timesheet response is received
    useEffect(() => {
        if (timesheetResponse && showSuccess) {
            setShowCostFeedback(true);
            // Auto-hide the feedback after 5 seconds
            const timer = setTimeout(() => {
                setShowCostFeedback(false);
                dispatch(clearTimesheetResponse());
            }, 5000);
            return () => clearTimeout(timer);
        }
    }, [timesheetResponse, showSuccess, dispatch]);

    const handleDateChange = (fieldName, date) => {
        if (date && date.startDate) {
            const startDate = new Date(date.startDate);
            const formattedDate = startDate.toISOString().split('T')[0];
            formik.setFieldValue(fieldName, formattedDate);
        }
    };

    const initialValues = {
        jobNumber: projectNumber,
        subcontractor: subcontractorName,
        hours: editMode && existingEntry ? existingEntry.hours : "",
        stage: editMode && existingEntry ? existingEntry.stage : "",
        comment: editMode && existingEntry ? existingEntry.comment : "",
        date: editMode && existingEntry ? existingEntry.date : today,
        UserEmail: account.username,
    };

    const validationSchema = Yup.object().shape({
        subcontractor: Yup.string().required("Subcontractor is required"),
        hours: Yup.number()
            .required("Hours required")
            .min(-100, "Minimum -100 hours")
            .max(100, "Maximum 100 hours"),
        stage: Yup.string().required("Stage is required"),
        date: Yup.string().required("Date is required"),
    });

    const formik = useFormik({
        initialValues: initialValues,
        validationSchema: validationSchema,
        onSubmit: async (values) => {
            if (editMode && existingEntry) {
                // Update existing entry
                onSubmit(values, 'update', existingEntry.id);
            } else {
                // Create new entry
                onSubmit(values, 'create');
            }
            // Don't close immediately - wait for cost feedback
            if (!timesheetResponse) {
                // Close after a delay to show cost feedback
                setTimeout(() => {
                    onClose();
                }, 6000);
            }
        },
    });

    const handleDelete = () => {
        if (editMode && existingEntry) {
            onSubmit(null, 'delete', existingEntry.id);
            setShowDeleteConfirmation(false);
            onClose();
        }
    };

    const { touched, errors, handleSubmit } = formik;

    const formatCurrency = (value) => {
        if (value === null || value === undefined || value === "") return "£0.00";
        const number = parseFloat(value);
        if (isNaN(number)) return "£0.00";
        return number.toLocaleString('en-GB', {
            style: 'currency',
            currency: 'GBP',
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        });
    };

    const renderDateField = (label, fieldName) => (
        <Grid container sx={{ width: "100%" }}>
            <Grid item xs={3} sx={{ pr: 3 }}>
                <p className={styles.dataTitleV2}>{label}: </p>
            </Grid>
            <Grid item xs={9} sx={{ mt: 1 }}>
                <DatePicker
                    initialDate={formik.values[fieldName]}
                    onDatesChange={(date) => handleDateChange(fieldName, date)}
                    visibleIcon={true}
                />
                {touched[fieldName] && errors[fieldName] && (
                    <p style={{ color: "red" }}>{errors[fieldName]}</p>
                )}
            </Grid>
        </Grid>
    );

    const handleCloseFeedback = () => {
        setShowCostFeedback(false);
        dispatch(clearTimesheetResponse());
        onClose();
    };

    return (
        <div className={styles.backGround} style={{ position: "fixed", alignItems: "flex-start" }}>
            <div className={styles.form} style={{ width: "500px" }}>
                {/* Internal Cost Feedback */}
                <Collapse in={showCostFeedback}>
                    <Box sx={{ mb: 2 }}>
                        <Alert 
                            severity="success" 
                            icon={<CheckCircleIcon fontSize="inherit" />}
                            onClose={handleCloseFeedback}
                            sx={{
                                '& .MuiAlert-message': {
                                    fontFamily: 'PT Sans',
                                    fontSize: '14px'
                                }
                            }}
                        >
                            <Box>
                                <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>
                                    {editMode ? 'Timesheet Updated Successfully!' : 'Timesheet Submitted Successfully!'}
                                </div>
                                {timesheetResponse && (
                                    <div>
                                        Internal cost updated: <strong>{formatCurrency(timesheetResponse.internalCostUpdated)}</strong>
                                    </div>
                                )}
                            </Box>
                        </Alert>
                    </Box>
                </Collapse>

                <div className={styles.momentTitle}>MOMENT</div>
                <div className={styles.sectionTitle}>
                    {editMode ? 'Editing timesheet entry for a subcontractor' : 'Adding time to a project for a subcontractor'}
                </div>

                <FormikProvider value={formik}>
                    <Form autoComplete="off" noValidate style={{ width: "100%" }}>
                        {/* Job Number */}
                        <Grid container sx={{ width: "100%", mt: 2 }}>
                            <Grid item xs={3} sx={{ pr: 3 }}>
                                <p className={styles.dataTitleV2}>Job Number: </p>
                            </Grid>
                            <Grid item xs={9}>
                                <TextField
                                    fullWidth
                                    value={formik.values.jobNumber}
                                    inputProps={{
                                        style: {
                                            color: formik.values.jobNumber ? "black" : "inherit", // Text black only if enabled
                                            cursor: "not-allowed", // Block cursor when disabled
                                        },
                                        disabled: true, // Disable the field
                                    }}
                                    sx={textfieldStyle}
                                />
                            </Grid>
                        </Grid>

                        {/* Address */}
                        <Grid container sx={{ width: "100%", mt: 2 }}>
                            <Grid item xs={3} sx={{ pr: 3 }}>
                                <p className={styles.dataTitleV2}>Address: </p>
                            </Grid>
                            <Grid item xs={9} >
                                <TextField
                                    fullWidth
                                    value={projectAddress}
                                    onChange={(e) => formik.setFieldValue("address", e.target.value)}
                                    inputProps={{
                                        style: {
                                            color: formik.values.jobNumber ? "black" : "inherit", // Text black only if enabled
                                            cursor: "not-allowed", // Block cursor when disabled
                                        },
                                        disabled: true, // Disable the field
                                    }}
                                    sx={textfieldStyle}
                                />
                            </Grid>
                        </Grid>

                        {/* Subcontractor */}
                        <Grid container sx={{ width: "100%", mt: 2 }}>
                            <Grid item xs={3} sx={{ pr: 3 }}>
                                <p className={styles.dataTitleV2}>Subcontractor: </p>
                            </Grid>
                            <Grid item xs={9}>
                                <TextField
                                    fullWidth
                                    value={formik.values.subcontractor}
                                    onChange={(e) => formik.setFieldValue("subcontractor", e.target.value)}
                                    inputProps={{
                                        style: {
                                            color: formik.values.jobNumber ? "black" : "inherit", // Text black only if enabled
                                            cursor: "not-allowed", // Block cursor when disabled
                                        },
                                        disabled: true, // Disable the field
                                    }}
                                    sx={textfieldStyle}
                                    error={Boolean(touched.subcontractor && errors.subcontractor)}
                                />
                            </Grid>
                        </Grid>

                        {/* Hours */}
                        <Grid container sx={{ width: "100%", mt: 2 }}>
                            <Grid item xs={3} sx={{ pr: 3 }}>
                                <p className={styles.dataTitleV2}>Hours: </p>
                            </Grid>
                            <Grid item xs={9}>
                                <TextField
                                    fullWidth
                                    type="number"
                                    value={formik.values.hours}
                                    onChange={(e) => formik.setFieldValue("hours", e.target.value)}
                                    placeholder="XXXX HRS"
                                    sx={textfieldStyle}
                                    error={Boolean(touched.hours && errors.hours)}
                                />
                            </Grid>
                        </Grid>

                        {/* Stage */}
                        <Grid container sx={{ width: "100%", mt: 2 }}>
                            <Grid item xs={3} sx={{ pr: 3 }}>
                                <p className={styles.dataTitleV2}>Stage: </p>
                            </Grid>
                            <Grid item xs={9} style={{ marginTop: '8px' }}>
                                <Dropdown
                                    placeholder={"Please select a stage"}
                                    options={phaseList}
                                    initialSelectedValue={formik.values.stage}
                                    onValueChange={(newValue) => formik.setFieldValue("stage", newValue)}
                                    PopUp={true}
                                    errorState={Boolean(touched.stage && errors.stage)}
                                />
                            </Grid>
                        </Grid>

                        {/* Date */}
                        {renderDateField("Date", "date")}

                        {/* Comment */}
                        <Grid container sx={{ width: "100%", mt: 2 }}>
                            <Grid item xs={3} sx={{ pr: 3 }}>
                                <p className={styles.dataTitleV2}>Comment: </p>
                            </Grid>
                            <Grid item xs={9}>
                                <TextField
                                    fullWidth
                                    multiline
                                    value={formik.values.comment}
                                    onChange={(e) => formik.setFieldValue("comment", e.target.value)}
                                    placeholder="Please enter a comment if required"
                                    sx={textfieldStyle}
                                />
                            </Grid>
                        </Grid>
                    </Form>
                </FormikProvider>

                {/* Buttons */}
                <div className={styles.updateAndCancel}>
                    <div className={styles.updateButton} onClick={handleSubmit}>
                        <div className={buttonStyles.YesButtonComponentStyling}>
                            {editMode ? 'UPDATE' : 'SUBMIT'}
                        </div>
                    </div>
                    {editMode && (
                        <div 
                            className={styles.updateButton} 
                            onClick={() => setShowDeleteConfirmation(true)}
                            style={{ backgroundColor: '#dc3545' }}
                        >
                            <div className={buttonStyles.YesButtonComponentStyling}>DELETE</div>
                        </div>
                    )}
                    <div className={styles.cancelButton} onClick={onClose}>
                        <div className={buttonStyles.NoButtonComponentStyling}>CANCEL</div>
                    </div>
                </div>

                {/* Delete Confirmation */}
                {showDeleteConfirmation && (
                    <div className={styles.confirmationOverlay}>
                        <div className={styles.confirmationBox}>
                            <p>Are you sure you want to delete this timesheet entry?</p>
                            <div className={styles.confirmationButtons}>
                                <button onClick={handleDelete} className={styles.confirmButton}>
                                    Yes, Delete
                                </button>
                                <button 
                                    onClick={() => setShowDeleteConfirmation(false)} 
                                    className={styles.cancelConfirmButton}
                                >
                                    Cancel
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                <img className={styles.blueLogo} src={BlueLogo} alt="BlueLogo" />
            </div>
        </div>
    );
};

export default TimesheetSubconForm;